import React, { forwardRef } from "react";
import styled from "styled-components";
import { tokens } from "../../public/tokens";

const sizes = {
    xs: {
        padding: "4px 8px",
        fontSize: tokens.typography.fontSize["md-12px"],
        lineHeight: "16px",
        borderRadius: "6px",
        minHeight: "28px",
    },
    sm: {
        padding: "4px 10px",
        fontSize: tokens.typography.fontSize["md-12px"],
        lineHeight: "16px",
        borderRadius: "6px",
        minHeight: "32px",
    },
    md: {
        padding: "6px 10px",
        fontSize: tokens.typography.fontSize["lg-14px"],
        lineHeight: "20px",
        borderRadius: "7px",
        minHeight: "36px",
    },
    lg: {
        padding: "8px 10px",
        fontSize: tokens.typography.fontSize["lg-14px"],
        lineHeight: "20px",
        borderRadius: "7px",
        minHeight: "40px",
    },
};

const defaultFocusVisibleState = {
    outline: `1px solid ${tokens.colors.brand.blue["500"]}`,
    boxShadow: "0px 0px 0px 1px rgba(163, 163, 163, 0.50)",
};

const errorState = {
    outline: `1px solid ${tokens.colors.ui.destructive}`,
    boxShadow: "0px 0px 1px 1px rgba(196, 61, 81, 0.20)",
};

const StyledDateInput = styled.input(({ $size = "lg" }) => {
    const {
        padding,
        fontSize,
        lineHeight,
        borderRadius,
        minHeight,
    } = sizes[$size];

    return {
        width: "100%",
        fontFamily: tokens.typography.fontFamilies["albert-sans"],
        fontWeight: tokens.typography.fontWeights.regular,
        transition: "all 0.2s ease",
        boxShadow: "0px 0px 0px 1px rgba(163, 163, 163, 0.50)",
        padding,
        fontSize,
        lineHeight,
        borderRadius,
        minHeight,
        background: tokens.colors.ui.background,
        border: `1px solid ${tokens.colors.ui.border}`,
        color: tokens.colors.ui.foreground,

        "&:hover:not(:disabled)": {
            borderColor: tokens.colors.brand.blue["500"],
        },

        "&:focus-visible:not(:disabled)": {
            ...defaultFocusVisibleState,
        },

        '&[data-error="true"]': {
            ...errorState,
        },

        "&:disabled": {
            opacity: 0.5,
            cursor: "not-allowed",
            background: tokens.colors.brand.blue["100"],
        },

        "&:focus": {
            outline: "none",
        },

        "&::placeholder": {
            color: tokens.colors.ui["muted-foreground"],
            fontWeight: tokens.typography.fontWeights.regular,
        },

        "&::-webkit-calendar-picker-indicator": {
            opacity: 0.6,
            cursor: "pointer",
        },

        "&::-webkit-datetime-edit": {
            padding: 0,
        },
    };
});

export const FieldWrapper = styled.div({
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    width: "100%",
});

export const Label = styled.label({
    fontSize: tokens.typography.fontSize["lg-14px"],
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontWeight: tokens.typography.fontWeights.medium,
    lineHeight: "20px",
    color: tokens.colors.ui.foreground,
    display: "flex",
    alignItems: "center",
    gap: "4px",

    '&[data-required="true"]::after': {
        content: '"*"',
        color: tokens.colors.ui.destructive,
        marginLeft: "2px",
    },
});

export const ErrorText = styled.span({
    fontSize: tokens.typography.fontSize["md-12px"],
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontWeight: tokens.typography.fontWeights.medium,
    lineHeight: "16px",
    color: tokens.colors.ui.destructive,
    minHeight: "1.4em",
});

export const HelperText = styled.span({
    fontSize: tokens.typography.fontSize["md-12px"],
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontWeight: tokens.typography.fontWeights.regular,
    lineHeight: "16px",
    color: tokens.colors.ui["muted-foreground"],
});

/**
 * A date input component with label, error, and helper text support.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.placeholder="DD/MM/YYYY"] - Placeholder text for the input
 * @param {string} [props.size="lg"] - Size variant: "xs", "sm", "md", or "lg"
 * @param {boolean} [props.error=false] - If true, applies error styling
 * @param {boolean} [props.disabled=false] - If true, the input is disabled
 * @param {string} [props.label] - Label text for the input
 * @param {boolean} [props.required] - If true, shows required asterisk
 * @param {string} [props.errorText] - Error message to display
 * @param {string} [props.helperText] - Helper text to display below the input
 * @param {React.Ref} ref - Forwarded ref to the underlying input element
 * @returns {JSX.Element} The rendered date input component
 */
const DateInput = forwardRef(
    (
        {
            placeholder = "DD/MM/YYYY",
            size = "lg",
            error = false,
            disabled = false,
            label,
            required,
            errorText,
            helperText,
            ...props
        },
        ref
    ) => {
        return (
            <FieldWrapper>
                {label && (
                    <Label data-required={required ? "true" : undefined}>
                        {label}
                    </Label>
                )}

                <StyledDateInput
                    ref={ref}
                    type="date"
                    $size={size}
                    data-error={error ? "true" : undefined}
                    disabled={disabled}
                    placeholder={placeholder}
                    {...props}
                />

                {errorText && (
                    <ErrorText>{errorText}</ErrorText>
                )}

                {helperText && !errorText && (
                    <HelperText>{helperText}</HelperText>
                )}
            </FieldWrapper>
        );
    }
);

DateInput.displayName = "DateInput";

export default DateInput;